/**
 * AuditLogDetail — Side sheet for a single audit log entry.
 *
 * Design: clean sectioned layout, no tabs.
 * Shows action header, performer, target entity, network info,
 * and an inline before/after diff or key-value details block.
 */
import { useState } from 'react';
import { Sheet, SheetContent, getAvatarColor } from '@respectly/ui';
import {
  X,
  Copy,
  Check,
  LogIn,
  LogOut,
  Plus,
  Pencil,
  Trash2,
  Ban,
  RefreshCw,
  XCircle,
  UserCog,
  Key,
  Settings,
  Trash,
  FileText,
  Globe,
  Monitor,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import type { SuperAdminAuditLog } from '@respectly/shared-types';

// ─── Action config ────────────────────────────────────────────────────────────

const ACTION_CONFIG: Record<
  string,
  { icon: React.ElementType; color: string; bg: string; ring: string; label: string }
> = {
  login:              { icon: LogIn,     color: 'text-blue-600 dark:text-blue-400',     bg: 'bg-blue-50 dark:bg-blue-900/25',      ring: 'ring-blue-200 dark:ring-blue-700/50',    label: 'Login' },
  logout:             { icon: LogOut,    color: 'text-slate-500 dark:text-slate-400',   bg: 'bg-slate-100 dark:bg-slate-800',       ring: 'ring-slate-200 dark:ring-slate-700',     label: 'Logout' },
  create:             { icon: Plus,      color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/25', ring: 'ring-emerald-200 dark:ring-emerald-700/50', label: 'Create' },
  update:             { icon: Pencil,    color: 'text-amber-600 dark:text-amber-400',   bg: 'bg-amber-50 dark:bg-amber-900/25',     ring: 'ring-amber-200 dark:ring-amber-700/50',  label: 'Update' },
  delete:             { icon: Trash2,    color: 'text-red-600 dark:text-red-400',       bg: 'bg-red-50 dark:bg-red-900/25',         ring: 'ring-red-200 dark:ring-red-700/50',      label: 'Delete' },
  suspend:            { icon: Ban,       color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/25',   ring: 'ring-orange-200 dark:ring-orange-700/50', label: 'Suspend' },
  reactivate:         { icon: RefreshCw, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/25', ring: 'ring-emerald-200 dark:ring-emerald-700/50', label: 'Reactivate' },
  cancel:             { icon: XCircle,   color: 'text-red-600 dark:text-red-400',       bg: 'bg-red-50 dark:bg-red-900/25',         ring: 'ring-red-200 dark:ring-red-700/50',      label: 'Cancel' },
  impersonate:        { icon: UserCog,   color: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-50 dark:bg-violet-900/25',   ring: 'ring-violet-200 dark:ring-violet-700/50', label: 'Impersonate' },
  password_reset:     { icon: Key,       color: 'text-cyan-600 dark:text-cyan-400',     bg: 'bg-cyan-50 dark:bg-cyan-900/25',       ring: 'ring-cyan-200 dark:ring-cyan-700/50',    label: 'Password Reset' },
  toggle_maintenance: { icon: Settings,  color: 'text-amber-600 dark:text-amber-400',   bg: 'bg-amber-50 dark:bg-amber-900/25',     ring: 'ring-amber-200 dark:ring-amber-700/50',  label: 'Toggle Maintenance' },
  clear_cache:        { icon: Trash,     color: 'text-slate-500 dark:text-slate-400',   bg: 'bg-slate-100 dark:bg-slate-800',       ring: 'ring-slate-200 dark:ring-slate-700',     label: 'Clear Cache' },
};

const ENTITY_LABELS: Record<string, string> = {
  tenant: 'Account',
  user: 'User',
  plan: 'Plan',
  template: 'Template',
  super_admin_user: 'Admin User',
  feature_flag: 'Feature Flag',
  platform: 'Platform',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatFull(date: Date | string) {
  return new Date(date).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function initials(first: string, last: string) {
  return `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase();
}

function detectBrowser(ua: string) {
  if (/Edg\//.test(ua)) return 'Edge';
  if (/Chrome\//.test(ua)) return 'Chrome';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Safari\//.test(ua) && !/Chrome/.test(ua)) return 'Safari';
  return 'Browser';
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500 mb-2.5">
        {title}
      </p>
      {children}
    </div>
  );
}

function MetaRow({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
      <span className="text-xs text-slate-500 dark:text-slate-400 shrink-0 pt-px">{label}</span>
      <span className="text-xs font-medium text-slate-900 dark:text-white text-right break-all">{value}</span>
    </div>
  );
}

/** Renders details: before/after diff if present, otherwise key-value pairs */
function DetailsBlock({ details }: { details: Record<string, unknown> }) {
  const [showRaw, setShowRaw] = useState(false);
  const [copied, setCopied] = useState(false);

  const hasDiff = details.before !== undefined && details.after !== undefined;
  const otherKeys = Object.entries(details).filter(([k]) => k !== 'before' && k !== 'after');

  const copy = () => {
    navigator.clipboard.writeText(JSON.stringify(details, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-3">
      {/* Before / After diff */}
      {hasDiff && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-red-500/80 dark:text-red-400/70 mb-1.5">
              Before
            </p>
            <div className="rounded-lg bg-red-50 dark:bg-red-900/15 border border-red-100 dark:border-red-800/40 p-2.5 overflow-auto max-h-36">
              <pre className="text-[11px] leading-relaxed text-red-700 dark:text-red-300 whitespace-pre-wrap font-mono">
                {JSON.stringify(details.before, null, 2)}
              </pre>
            </div>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600/80 dark:text-emerald-400/70 mb-1.5">
              After
            </p>
            <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/15 border border-emerald-100 dark:border-emerald-800/40 p-2.5 overflow-auto max-h-36">
              <pre className="text-[11px] leading-relaxed text-emerald-700 dark:text-emerald-300 whitespace-pre-wrap font-mono">
                {JSON.stringify(details.after, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Other key-value pairs */}
      {otherKeys.length > 0 && (
        <div className="rounded-lg border border-slate-100 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
          {otherKeys.map(([k, v]) => (
            <div key={k} className="flex items-start justify-between gap-3 px-3 py-2">
              <span className="text-xs text-slate-500 dark:text-slate-400 capitalize shrink-0">
                {k.replace(/_/g, ' ')}
              </span>
              <span className="text-xs font-medium text-slate-800 dark:text-slate-200 text-right break-all font-mono">
                {typeof v === 'object' ? JSON.stringify(v) : String(v ?? '—')}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Raw JSON toggle */}
      <button
        onClick={() => setShowRaw((s) => !s)}
        className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
      >
        {showRaw ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        {showRaw ? 'Hide raw JSON' : 'Show raw JSON'}
      </button>

      {showRaw && (
        <div className="relative">
          <button
            onClick={copy}
            className="absolute right-2 top-2 z-10 flex items-center gap-1 rounded px-1.5 py-1 text-xs text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 bg-slate-100 dark:bg-slate-700 transition-colors"
          >
            {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
          <div className="rounded-lg bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 p-3 pt-8 overflow-auto max-h-48">
            <pre className="text-[11px] leading-relaxed text-slate-600 dark:text-slate-300 whitespace-pre-wrap font-mono">
              {JSON.stringify(details, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface AuditLogDetailProps {
  log: SuperAdminAuditLog | null;
  isOpen: boolean;
  onClose: () => void;
}

export function AuditLogDetail({ log, isOpen, onClose }: AuditLogDetailProps) {
  if (!log) return null;

  const cfg = ACTION_CONFIG[log.action] ?? {
    icon: FileText,
    color: 'text-slate-600 dark:text-slate-400',
    bg: 'bg-slate-100 dark:bg-slate-800',
    ring: 'ring-slate-200 dark:ring-slate-700',
    label: log.action.replace(/_/g, ' '),
  };
  const ActionIcon = cfg.icon;

  const entityLabel = ENTITY_LABELS[log.entityType] ?? log.entityType;
  const hasDetails = !!log.details && Object.keys(log.details).length > 0;
  const performerName = log.user ? `${log.user.firstName} ${log.user.lastName}` : null;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="sm:max-w-md p-0 flex flex-col overflow-hidden">

        {/* ── Sticky header ── */}
        <div className="shrink-0 px-5 pt-5 pb-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ring-1 ${cfg.bg} ${cfg.ring}`}>
                <ActionIcon className={`h-5 w-5 ${cfg.color}`} />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-slate-900 dark:text-white capitalize">
                  {cfg.label}
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                  {entityLabel}
                  {log.entityName && (
                    <span className="text-slate-700 dark:text-slate-300"> · {log.entityName}</span>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="shrink-0 rounded-lg p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Timestamp under header */}
          <p className="mt-3 text-xs text-slate-400 dark:text-slate-500">
            {formatFull(log.createdAt)}
          </p>
        </div>

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">

          {/* Performed by */}
          <Section title="Performed by">
            {log.user ? (
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${getAvatarColor(performerName!)} text-white text-xs font-bold shadow-sm`}
                >
                  {initials(log.user.firstName, log.user.lastName)}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 dark:text-white leading-tight">
                    {performerName}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                    {log.user.email}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500 dark:text-slate-400">System</p>
            )}
          </Section>

          {/* Target entity */}
          <Section title="Target">
            <div className="rounded-lg border border-slate-100 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
              <MetaRow label="Type" value={entityLabel} />
              {log.entityName && <MetaRow label="Name" value={log.entityName} />}
              {log.entityId && (
                <MetaRow
                  label="ID"
                  value={<span className="font-mono text-[11px]">{log.entityId}</span>}
                />
              )}
            </div>
          </Section>

          {/* Network */}
          {(log.ipAddress || log.userAgent) && (
            <Section title="Network">
              <div className="rounded-lg border border-slate-100 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
                {log.ipAddress && (
                  <MetaRow
                    label={
                      <span className="flex items-center gap-1">
                        <Globe className="h-3 w-3" /> IP
                      </span>
                    }
                    value={<span className="font-mono">{log.ipAddress}</span>}
                  />
                )}
                {log.userAgent && (
                  <MetaRow
                    label={
                      <span className="flex items-center gap-1">
                        <Monitor className="h-3 w-3" /> Client
                      </span>
                    }
                    value={detectBrowser(log.userAgent)}
                  />
                )}
              </div>
            </Section>
          )}

          {/* Details */}
          {hasDetails && (
            <Section title="Details">
              <DetailsBlock details={log.details as Record<string, unknown>} />
            </Section>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="shrink-0 border-t border-slate-100 dark:border-slate-800 px-5 py-3">
          <p className="text-[11px] text-slate-400 dark:text-slate-500 font-mono truncate">
            {log.id}
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
